import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { Input, VStack } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";

import { ZoneType } from "@/codegen/openapi2";
import { Button } from "@/codegen/ui/button";
import { Field } from "@/codegen/ui/field";
import { toaster } from "@/codegen/ui/toaster";
import { Modal } from "@/components/2/modal";
import {
  useCreateZoneGeographiesZonesPost,
  useGetLegalEntityLegalEntitiesGet,
  useGetRegionsGeographiesRegionsGet,
} from "@/services/2/react-query/hooks";

import { ZoneCreationSchema } from "./zone-creation-schema";

type ZoneCreateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const ZoneCreateModal = ({ isOpen, onClose, onSuccess }: ZoneCreateModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(ZoneCreationSchema),
    defaultValues: {
      name: "",
      region_uuid: "",
      legal_entity_uuid: "",
      customer_service_id: "",
      aircall_phone_number: "",
      aircall_main_number_id: "",
    },
  });

  const selectedLegalEntityUuid = watch("legal_entity_uuid");
  const queryLegalEntities = selectedLegalEntityUuid ? [selectedLegalEntityUuid] : null;

  const { data: regions } = useGetRegionsGeographiesRegionsGet<ZoneType[]>(
    { query: { legal_entity_uuids: queryLegalEntities } },
    ["regions", queryLegalEntities?.join(",") || "all"],
    {
      placeholderData: [],
    }
  );

  // Reset region selection when legal entity changes
  useEffect(() => {
    setValue("region_uuid", "");
  }, [selectedLegalEntityUuid, setValue]);

  const { data: legalEntities } = useGetLegalEntityLegalEntitiesGet({}, ["legalEntities"], {
    placeholderData: [],
  });

  const { mutate: createZone, isPending } = useCreateZoneGeographiesZonesPost();

  const onSubmit = (data: any) => {
    console.log("Form submitted with data:", data);

    createZone(
      {
        body: data,
      },
      {
        onSuccess: () => {
          toaster.create({
            title: "Zone créée avec succès",
            type: "success",
          });
          handleClose();
          onSuccess();
        },
        onError: () => {
          toaster.create({
            title: "Erreur lors de la création de la zone",
            description: "Veuillez réessayer ou contacter le support si le problème persiste",
            type: "error",
          });
        },
      }
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={open => !open && handleClose()}
      title="Créer une nouvelle zone"
      content={
        <form onSubmit={handleSubmit(onSubmit)} id="zone-create-form">
          <VStack gap={4}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Field label="Nom" invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input {...field} placeholder="Nom de la zone" />
                </Field>
              )}
            />

            <Controller
              name="legal_entity_uuid"
              control={control}
              render={({ field }) => (
                <Field
                  label="Entité légale"
                  invalid={!!errors.legal_entity_uuid}
                  errorText={errors.legal_entity_uuid?.message}
                >
                  <select
                    {...field}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  >
                    <option value="">Sélectionner une entité légale</option>
                    {legalEntities?.map(legalEntity => (
                      <option key={legalEntity.uuid} value={legalEntity.uuid}>
                        {legalEntity.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            />

            <Controller
              name="region_uuid"
              control={control}
              render={({ field }) => (
                <Field
                  label="Région"
                  invalid={!!errors.region_uuid}
                  errorText={errors.region_uuid?.message}
                >
                  <select
                    {...field}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  >
                    <option value="">Sélectionner une région</option>
                    {regions?.map(region => (
                      <option key={region.uuid} value={region.uuid}>
                        {region.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            />

            <Controller
              name="customer_service_id"
              control={control}
              render={({ field }) => (
                <Field
                  label="ID Service Client (Optionnel)"
                  invalid={!!errors.customer_service_id}
                  errorText={errors.customer_service_id?.message}
                >
                  <Input
                    {...field}
                    value={field.value || ""}
                    type="number"
                    placeholder="ID Service"
                  />
                </Field>
              )}
            />

            <Controller
              name="aircall_phone_number"
              control={control}
              render={({ field }) => (
                <Field
                  label="Numéro de téléphone Aircall Coordo"
                  invalid={!!errors.aircall_phone_number}
                  errorText={errors.aircall_phone_number?.message}
                >
                  <Input {...field} placeholder="Numéro de téléphone (ex: +33123456789)" />
                </Field>
              )}
            />

            <Controller
              name="aircall_main_number_id"
              control={control}
              render={({ field }) => (
                <Field
                  label="ID Numéro Principal Aircall Coordo"
                  invalid={!!errors.aircall_main_number_id}
                  errorText={errors.aircall_main_number_id?.message}
                >
                  <Input {...field} type="number" placeholder="ID Numéro principal Coordo" />
                </Field>
              )}
            />
            <Controller
              name="aircall_rh_number"
              control={control}
              render={({ field }) => (
                <Field
                  label="Numéro de téléphone Aircall RH"
                  invalid={!!errors.aircall_rh_number}
                  errorText={errors.aircall_rh_number?.message}
                >
                  <Input {...field} placeholder="Numéro de téléphone (ex: +33123456789)" />
                </Field>
              )}
            />

            <Controller
              name="aircall_main_rh_number_id"
              control={control}
              render={({ field }) => (
                <Field
                  label="ID Numéro Principal Aircall RH"
                  invalid={!!errors.aircall_main_rh_number_id}
                  errorText={errors.aircall_main_rh_number_id?.message}
                >
                  <Input {...field} type="number" placeholder="ID Numéro principal RH" />
                </Field>
              )}
            />
          </VStack>
        </form>
      }
      footer={
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={handleClose}>
            Annuler
          </Button>
          <Button type="submit" form="zone-create-form" loading={isPending || isSubmitting}>
            Créer la zone
          </Button>
        </div>
      }
    />
  );
};
